import { ButtonGroup } from '@antimatterhq/ui/components/buttonGroup';
import { Label } from '@antimatterhq/ui/components/label';
import Spacer from '@antimatterhq/ui/components/spacer';
import { Text } from '@antimatterhq/ui/components/typography';
import { useFormikContext } from 'formik';
import { WorkspaceConfigAlertsSchemaValues } from '@/zod/schemas/workspaceConfig/AlertSchema';
import { FormError, FormInput, FormTextarea } from '@/components/form/form';
import {
  workspaceConfigJiraEmailTooltip,
  workspaceConfigJiraIssueTypeTooltip,
  workspaceConfigJiraProjectTooltip,
  workspaceConfigJiraServerTooltip,
  workspaceConfigJiraSummaryTooltip,
  workspaceConfigJiraTokenTooltip,
} from '@/strings/StringsTooltips';

type JiraAlertProps = {
  formPrefix: 'notables' | 'operational';
  disabled: boolean;
};

export const JiraAlerts = ({ formPrefix, disabled }: JiraAlertProps) => {
  const {
    values: baseValues,
    handleBlur,
    handleChange,
  } = useFormikContext<WorkspaceConfigAlertsSchemaValues>();
  const values =
    formPrefix === 'notables' ? baseValues.notables : baseValues.operational;

  return (
    <>
      <Text size="sm">
        {formPrefix === 'notables' ? 'Notables' : 'Operational alerts'} to Jira
        config
      </Text>

      <Spacer size="base" />

      <ButtonGroup
        name={`${formPrefix}.jira.tokenType`}
        value={values.jira.tokenType}
        options={[
          { label: 'Token', value: 'token' },
          { label: 'Token from secret', value: 'secret' },
        ]}
        onChange={(value) =>
          handleChange({
            target: { name: `${formPrefix}.jira.tokenType`, value },
          })
        }
        disabled={disabled}
      />

      <Spacer size="base" />

      {values.jira.tokenType === 'token' && (
        <>
          <Label
            htmlFor={`${formPrefix}.jira.token.value`}
            id={`${formPrefix}.jira.token.value-label`}
            required
            info={workspaceConfigJiraTokenTooltip}
          >
            Jira API token
          </Label>
          <FormInput
            aria-labelledby={`${formPrefix}.jira.token.value-label`}
            type="text"
            name={`${formPrefix}.jira.token.value`}
            value={values.jira.token.value}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Jira API token"
            disabled={disabled}
          />
          <FormError name={`${formPrefix}.jira.token.value`} />
        </>
      )}

      {values.jira.tokenType === 'secret' && (
        <>
          <Label
            htmlFor={`${formPrefix}.jira.token.key`}
            id={`${formPrefix}.jira.token.key-label`}
            required
          >
            Databricks secret key
          </Label>
          <FormInput
            aria-labelledby={`${formPrefix}.jira.token.key-label`}
            type="text"
            name={`${formPrefix}.jira.token.key`}
            value={values.jira.token.key}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Secret key"
            disabled={disabled}
          />
          <FormError name={`${formPrefix}.jira.token.key`} />

          <Spacer size="base" />

          <Label
            htmlFor={`${formPrefix}.jira.token.scope`}
            id={`${formPrefix}.jira.token.scope-label`}
            required
          >
            Databricks secret scope
          </Label>
          <FormInput
            aria-labelledby={`${formPrefix}.jira.token.scope-label`}
            type="text"
            name={`${formPrefix}.jira.token.scope`}
            value={values.jira.token.scope}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Secret scope"
            disabled={disabled}
          />
          <FormError name={`${formPrefix}.jira.token.scope`} />
        </>
      )}

      <Spacer size="base" />

      <Label
        htmlFor={`${formPrefix}.jira.email`}
        info={workspaceConfigJiraEmailTooltip}
        required
      >
        Jira email
      </Label>
      <FormInput
        type="text"
        name={`${formPrefix}.jira.email`}
        value={values.jira.email}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder="Email address of the token owner"
        disabled={disabled}
      />
      <FormError name={`${formPrefix}.jira.email`} />

      <Spacer size="base" />

      <Label
        htmlFor={`${formPrefix}.jira.server`}
        info={workspaceConfigJiraServerTooltip}
        required
      >
        Jira server
      </Label>
      <FormInput
        type="text"
        name={`${formPrefix}.jira.server`}
        value={values.jira.server}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder="Server URL"
        disabled={disabled}
      />
      <FormError name={`${formPrefix}.jira.server`} />

      <Spacer size="base" />

      <Label
        htmlFor={`${formPrefix}.jira.project`}
        info={workspaceConfigJiraProjectTooltip}
        required
      >
        Jira project
      </Label>
      <FormInput
        type="text"
        name={`${formPrefix}.jira.project`}
        value={values.jira.project}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder="Project name"
        disabled={disabled}
      />
      <FormError name={`${formPrefix}.jira.project`} />

      <Spacer size="base" />

      <Label
        htmlFor={`${formPrefix}.jira.issueType`}
        info={workspaceConfigJiraIssueTypeTooltip}
        required
      >
        Jira issue type
      </Label>
      <FormInput
        type="text"
        name={`${formPrefix}.jira.issueType`}
        value={values.jira.issueType}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder="(Task, Story, Bug, etc)"
        disabled={disabled}
      />
      <FormError name={`${formPrefix}.jira.issueType`} />

      <Spacer size="base" />

      <Label
        htmlFor={`${formPrefix}.jira.summary`}
        info={workspaceConfigJiraSummaryTooltip}
        required
      >
        Jira summary
      </Label>
      <FormTextarea
        name={`${formPrefix}.jira.summary`}
        value={values.jira.summary}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder="Summary"
        disabled={disabled}
      />
      <FormError name={`${formPrefix}.jira.summary`} />
    </>
  );
};
